import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../auth/firebaseConfig';
import { LivingInAreaData } from '../../types/livingInArea';
import {
  ErrorState,
  LoadingState
} from '../../components/living-in-area/LoadingState';
import { HeroSection } from '../../components/living-in-area/HeroSection';
import { LifestyleSection } from '../../components/living-in-area/LifestyleSection';
import { DayInLifeSection } from '../../components/living-in-area/DayInLifeSection';
import { TransportSection } from '../../components/living-in-area/TransportSection';
import { HousingSection } from '../../components/living-in-area/HousingSection';
import { CommunitySection } from '../../components/living-in-area/CommunitySection';
import { VibeSection } from '../../components/living-in-area/VibeSection';
import { RemoteWorkSection } from '../../components/living-in-area/RemoteWorkSection';
import { AmenitiesSection } from '../../components/living-in-area/AmenitiesSection';
import { IdealResidentsSection } from '../../components/living-in-area/IdealResidentsSection';
import { ProsConsSection } from '../../components/living-in-area/ProsConsSection';
import { CTASection } from '../../components/living-in-area/CTASection';
import ShareModal from '../../components/property/property-card/ShareModal';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

const LivingInArea: React.FC = () => {
  const { area } = useParams<{ area: string }>();
  const [data, setData] = useState<LivingInAreaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!area) return;
      const ref = doc(db, 'areaInsights', area);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setData(snap.data() as LivingInAreaData);
      }
      setLoading(false);
    };
    fetchData();
  }, [area]);

  if (loading) return <LoadingState />;
  if (error || !data)
    return (
      <ErrorState
        message={error || 'Data unavailable'}
        onRetry={() => window.location.reload()}
      />
    );

  // Share modal props
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = `Living in ${data.areaName} | RentSmart.ie`;

  return (
    <>
      <Helmet>
        <title>{data.seo.title}</title>
        <meta name="description" content={data.seo.description} />
        <meta property="og:title" content={data.seo.ogTitle} />
        <meta property="og:description" content={data.seo.ogDescription} />
        <meta name="keywords" content={data.seo.keywords.join(', ')} />
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] bg-slate-50 pb-16 ">
        <HeroSection data={data} />

        <main className="max-w-6xl mx-auto px-6 py-16 space-y-20">
          <LifestyleSection data={data.lifestyleOverview} />
          <DayInLifeSection data={data.dayInTheLife} areaName={data.areaName} />
          <TransportSection data={data.gettingAround} />
          <HousingSection data={data.housingReality} />
          <CommunitySection data={data.communityProfile} />
          <VibeSection data={data.areaVibe} />
          <RemoteWorkSection data={data.remoteWork} />
          <AmenitiesSection data={data.amenitiesSnapshot} />
          <IdealResidentsSection data={data.whoWillFeelMostAtHome} />
          <ProsConsSection data={data.livingProsCons} />
          <PseoEmailCapture
            pageType="living-in-area"
            pageSlug={data.areaSlug}
            locationName={data.areaName}
            contextHeadline={`Living In ${data.areaName}: Market Updates`}
            contextReason={`Receive Irish property market updates for ${data.areaName}, including local housing pressure shifts, neighborhood report updates, and tailored renter/landlord/agent insights.`}
          />
        </main>

        <CTASection data={data.nextStep} slug={data.areaSlug} />
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          url={shareUrl}
          title={shareTitle}
        />
      </div>
    </>
  );
};

export default LivingInArea;
